import { getLink } from "@utils/linkUtils";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import Link from "next/link";
import React, { FC } from "react";
import ReactFocusLock from "react-focus-lock";

import { NavigationItem } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";

interface Props {
  item: NavigationItem;
  active: boolean;
  transition: any;
  setDesktopPanelID: Function;
  trigger: any;
  setTrigger: Function;
}

const NavigationPanelCorp: FC<Props> = ({ item, active, transition, setDesktopPanelID, trigger, setTrigger }) => {
  return (
    <motion.div
      id={`modal-panel-desktop-${item.id}`}
      initial="close"
      variants={{
        open: { opacity: 1, transform: "translateY(0px)", display: "block" },
        close: { opacity: 0, transform: "translateY(-10px)", transitionEnd: { display: "none" } },
      }}
      animate={active ? "open" : "close"}
      transition={transition}
      className="absolute top-full left-0 z-50 pointer-events-auto"
    >
      <ReactFocusLock
        returnFocus={(el) => {
          if (!trigger) {
            setTrigger(el as HTMLElement);
          } else {
            trigger.focus();
            setTrigger(undefined);
          }
          return true;
        }}
        disabled={!active}
      >
        <PanelCorp setDesktopPanelID={setDesktopPanelID} item={item} />
      </ReactFocusLock>
    </motion.div>
  );
};

interface PanelProps {
  item: NavigationItem;
  setDesktopPanelID: Function;
}

const PanelCorp: FC<PanelProps> = ({ item, setDesktopPanelID }) => {
  const locale = useLocale();

  return (
    <div className="bg-white p-l pt-xs rounded-2xl shadow-[0_2px_24px_0_rgba(0,0,0,0.1)] mt-xs">
      <div className="w-[250px] max-h-[calc(100dvh-var(--header-height)-53px)] relative overflow-y-auto">
        {item && item.columns && item.columns.length > 0 && (
          <>
            <div className="sticky top-0 bg-white pt-l">
              {!item.menuItemCta && <p className="mb-m font-primary text-[14px] font-medium text-[#C6A47B]">{item.columns[0].title.toUpperCase()}</p>}
              {item.menuItemCta && (
                <div className="mb-m">
                  <Link href={getLink(item.menuItemCta, locale)} className="font-secondary text-s font-medium">
                    {item.menuItemCta.label}
                  </Link>
                </div>
              )}
              <hr className="border-t-gray-10" />
            </div>
            <div className="flex flex-col gap-0 justify-between mt-s">
              {item.columns.map((column) => (
                <ul key={"column-" + column.id}>
                  {column.links?.map((link, index) => (
                    <li key={`desktop-menu-link-${link.id}-${index}`}>
                      <Button
                        className="text-[14px] py-s w-full no-underline flex justify-between items-center font-secondary font-medium hover:underline"
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.BLACK}
                        href={getLink(link, locale)}
                      >
                        <>{link.label}</>
                      </Button>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </>
        )}
        <button className="absolute right-m top-m sr-only" onClick={() => setDesktopPanelID("")}>
          <Icon name="close" className="stroke-black" size="24" title="Close" />
        </button>
      </div>
    </div>
  );
};

export default NavigationPanelCorp;
